import { useRouter } from '@application/utils';
import {
  Button,
  CardItemBreed,
  CardItemBreeder,
  CardItemContainer,
  CardItemLitter,
  CardItemWrapper,
  Container,
  JoinBanner,
  PressSection,
  ScrollArowAnimated,
  Section,
  SectionCardItemSlider,
  SectionTitle,
  Banner,
  StoreBanner,
} from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import { useMemo } from 'react';
import { TbArrowRight } from 'react-icons/tb';
import { Link } from 'react-router-dom';
import bannerBackgroundImage from '@assets/banner-background-image.svg';
import { CountryCode } from '@gql/graphql';
import pierreBackground from './assets/Pierre.jpg';
import zoeBackground from './assets/Zoe.webp';
import dogHome from './assets/dog-home.webp';
import separatorBlueBottom from './assets/marker-section-bot.svg';
import separatorBlueTop from './assets/marker-section-top.svg';
import bannerImage from './assets/banner-grid.webp';
import * as Styled from './styled';

import type { HomeLandingPageProps } from './types';

export * from './types';

export const HomeLandingPage: FC<HomeLandingPageProps> = (props) => {
  const {
    joinBanner,
    litterItems = [],
    breederItems = [],
    litterItemsLoading = false,
    breederItemsLoading = false,
    onContactClick,
    onMoreBreedersclick,
    onMoreLittersClick,
    onBuyClick,
    onSellClick,
    popularLitterCategoriesByBreed,
    popularBreederCategoriesByBreed,
    breedInterestBanner,
  } = props;
  const { routeLocationDescriptor, countryCode, navigateToRoute } = useRouter();
  const { translate } = useTranslation();

  const showCategories = useMemo(() => {
    if (countryCode === CountryCode.CH) {
      return false;
    }
    return (
      popularBreederCategoriesByBreed?.items?.length &&
      popularLitterCategoriesByBreed?.items?.length
    );
  }, [
    countryCode,
    popularBreederCategoriesByBreed?.items?.length,
    popularLitterCategoriesByBreed?.items?.length,
  ]);

  const litterCards = useMemo(() => {
    if (litterItemsLoading) {
      return Array(4)
        .fill(undefined)
        .map((_, index) => (
          <CardItemLitter
            style={{ height: 327.5 }}
            key={index}
            loading
            breed=""
            price=""
            location=""
          />
        ));
    }
    return litterItems.map((item, index) => (
      <CardItemLitter style={{ height: 327.5 }} key={index} {...item} lazyLoadImage={false} />
    ));
  }, [litterItems, litterItemsLoading]);

  const breederCards = useMemo(() => {
    if (breederItemsLoading) {
      return Array(4)
        .fill(undefined)
        .map((_, index) => <CardItemBreeder style={{ height: 375 }} key={index} loading />);
    }
    return breederItems.map((item, index) => (
      <CardItemBreeder style={{ height: 375 }} key={index} {...item} />
    ));
  }, [breederItems, breederItemsLoading]);

  return (
    <>
      <Styled.TopWrapper>
        <Styled.TopSection>
          <Styled.TopBackgroundImage>
            <img src={dogHome} alt="" />
          </Styled.TopBackgroundImage>

          <Styled.TopContent>
            <Styled.TopTitle>{translate('homeLanding.hero.title')}</Styled.TopTitle>
            <Styled.TopSubtitle>{translate('homeLanding.hero.subtitle')}</Styled.TopSubtitle>
            <Styled.TopButtonsContainer>
              <Button size="medium" shrink color="blue" onClick={onBuyClick}>
                {translate('common.buttons.buy')}
              </Button>
              <Button size="medium" shrink color="white" outlined onClick={onSellClick}>
                {translate('common.buttons.sell')}
              </Button>
            </Styled.TopButtonsContainer>
          </Styled.TopContent>

          <Styled.ScrollArrowWrapper>
            <ScrollArowAnimated />
          </Styled.ScrollArrowWrapper>
        </Styled.TopSection>
      </Styled.TopWrapper>

      <Container>
        <SectionCardItemSlider
          title={translate('common.sections.latestLitters')}
          items={litterCards}
          onShowMoreClick={onMoreLittersClick}
        />
      </Container>

      <Styled.BreederSection>
        <Container>
          <SectionCardItemSlider
            title={translate('common.sections.latestBreeders')}
            items={breederCards}
            onShowMoreClick={onMoreBreedersclick}
          />
        </Container>
      </Styled.BreederSection>

      <Container>
        <Banner
          title={translate('common.banners.quiz.title')}
          btnText={translate('common.buttons.takeTest')}
          theme="blue"
          backgroundImage={bannerBackgroundImage}
          image={bannerImage}
          onClick={() => navigateToRoute('quiz')}
        />
      </Container>

      {showCategories && (
        <>
          <Styled.CategorySection>
            <Container>
              <SectionTitle>{translate('homeLanding.litterCategories.title')}</SectionTitle>

              <CardItemContainer>
                {popularLitterCategoriesByBreed?.items.map((item, index) => (
                  <CardItemWrapper key={index} skipFullWidthPhone>
                    <CardItemBreed
                      name={item.name}
                      image={item.image}
                      link={item.link}
                      footerText={translate('common.buttons.seeLitters')}
                      dontTruncate
                      shrinkHeightMobile
                    />
                  </CardItemWrapper>
                ))}
              </CardItemContainer>

              {!popularLitterCategoriesByBreed?.showMore && (
                <Section centerChildren>
                  <Button
                    onClick={popularLitterCategoriesByBreed?.onShowMore}
                    shrink
                    color="blue"
                    outlined
                  >
                    {translate('common.buttons.seeMore')}
                  </Button>
                </Section>
              )}

              {popularLitterCategoriesByBreed?.showMore && (
                <Styled.CategoryLinks>
                  <Styled.LinksRows>
                    {popularLitterCategoriesByBreed?.moreItems.map((item, index) => (
                      <Link key={index} to={item.link}>
                        {item.name}
                      </Link>
                    ))}
                  </Styled.LinksRows>

                  <Styled.CategorySeeMoreWrapper>
                    <Button
                      shrink
                      iconRight={<TbArrowRight />}
                      link={routeLocationDescriptor('litterLinks')}
                    >
                      {translate('common.buttons.seeMore')}
                    </Button>
                  </Styled.CategorySeeMoreWrapper>
                </Styled.CategoryLinks>
              )}
            </Container>
          </Styled.CategorySection>

          <Styled.CategorySection>
            <Container>
              <SectionTitle>{translate('homeLanding.breederCategories.title')}</SectionTitle>

              <CardItemContainer>
                {popularBreederCategoriesByBreed?.items.map((item, index) => (
                  <CardItemWrapper key={index} skipFullWidthPhone>
                    <CardItemBreed
                      name={item.name}
                      image={item.image}
                      link={item.link}
                      footerText={translate('common.buttons.seeBreedings')}
                      dontTruncate
                      shrinkHeightMobile
                    />
                  </CardItemWrapper>
                ))}
              </CardItemContainer>

              {!popularBreederCategoriesByBreed?.showMore && (
                <Section centerChildren>
                  <Button
                    onClick={popularBreederCategoriesByBreed?.onShowMore}
                    shrink
                    color="blue"
                    outlined
                  >
                    {translate('common.buttons.seeMore')}
                  </Button>
                </Section>
              )}

              {popularBreederCategoriesByBreed?.showMore && (
                <Styled.CategoryLinks>
                  <Styled.LinksRows>
                    {popularBreederCategoriesByBreed?.moreItems.map((item, index) => (
                      <Link key={index} to={item.link}>
                        {item.name}
                      </Link>
                    ))}
                  </Styled.LinksRows>

                  <Styled.CategorySeeMoreWrapper>
                    <Button
                      shrink
                      iconRight={<TbArrowRight />}
                      link={routeLocationDescriptor('breederLinks')}
                    >
                      {translate('common.buttons.seeMore')}
                    </Button>
                  </Styled.CategorySeeMoreWrapper>
                </Styled.CategoryLinks>
              )}
            </Container>
          </Styled.CategorySection>
        </>
      )}

      <Styled.BannerContainer>
        <StoreBanner source="homepage" theme="lightBlue" />
      </Styled.BannerContainer>

      <Styled.ContactSection>
        <Styled.SectionSeparator src={separatorBlueTop} />
        <Styled.ContactContainer>
          <Styled.ContactImageContainer>
            <Styled.ContactImage src={zoeBackground} />
            <Styled.ContactImage src={pierreBackground} bottom />
          </Styled.ContactImageContainer>
          <Styled.ContactContent>
            <Styled.ContactTitle
              dangerouslySetInnerHTML={{ __html: translate('homeLanding.help.title') }}
            />
            <Styled.Subtitle
              dangerouslySetInnerHTML={{ __html: translate('homeLanding.help.text') }}
            />
            <Styled.ContactButtonWrapper>
              <Button color="blue" shrink onClick={onContactClick}>
                {translate('common.buttons.contact')}
              </Button>
            </Styled.ContactButtonWrapper>
          </Styled.ContactContent>
        </Styled.ContactContainer>
        <Styled.SectionSeparatorBottom src={separatorBlueBottom} />
      </Styled.ContactSection>

      <PressSection />

      {breedInterestBanner && (
        <Styled.BreedInterestSection>
          <Container>{breedInterestBanner}</Container>
        </Styled.BreedInterestSection>
      )}

      <Styled.JoinBannerWrapper>
        <Styled.JoinBannerTitle
          dangerouslySetInnerHTML={{ __html: translate('common.sections.registration') }}
        />
        <JoinBanner {...joinBanner} />
      </Styled.JoinBannerWrapper>
    </>
  );
};
